import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Layers, Wallet, CheckCircle2, MapPin, ArrowRight, AlertTriangle, X } from 'lucide-react';
import { MetricCard, SectionHeader, EmptyState, LoadError, PageSkeleton } from '@/components/dashboard/MetricCard';
import { dashboardApi } from '@/services/dashboardApi';
import { fmtMoney } from '@/lib/format';

/*
  Mis operaciones (decisión de Samuel, 16/10/2026; sustituye a «Rendimiento»).
  Para todos los socios (Access, PRO y Premium):
  - Lo aportado a cada operación del club, su estado y la parte del resultado que le corresponde.
  - El resultado solo aparece cuando la operación se cierra: nunca se muestran estimaciones.
  - El saldo aún no asignado a ninguna operación, en la divisa en la que se pagó (W2USDT en dólares).
  - Retiros (17/10/2026): al cerrar una operación, principal e intereses vuelven al saldo. Los intereses se
    pueden retirar siempre; el principal, a los 12 meses de la aportación (el bono de membresía, nunca).
    Si el principal tiene bricks, el socio tiene que confirmar que los pierde.
  Lenguaje: aportación, participación, resultado. Nada de inversión, rentabilidad ni tokens.
*/
const money = (v, cur) => fmtMoney(v, String(cur).toUpperCase() === 'EUR' ? 'eur' : 'usd');
const joinMoney = (list) => (list?.length ? list.map((x) => money(x.amount, x.currency)).join(' · ') : '0 €');
// Total por divisa (el saldo puede venir de varios orígenes en la misma divisa)
const byCurrency = (list) => Object.entries((list || []).reduce((a, x) => ({ ...a, [x.currency]: (a[x.currency] || 0) + x.amount }), {}))
  .map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }));
const fdate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};
const BUCKET_NOTE = {
  real: 'Lo que has aportado y no está en ninguna operación en curso.',
  membresia: 'Incluido con tu membresía. Puedes destinarlo a una operación; no se puede retirar.',
  w2usdt: 'Saldo en W2USDT, expresado en dólares.',
  intereses: 'Tu parte del resultado de las operaciones cerradas. Puedes retirarlos cuando quieras.',
};
const W_STATUS = {
  Solicitada: 'text-amber-200 border-amber-400/20 bg-amber-400/10',
  Aprobada: 'text-sky-200 border-sky-400/20 bg-sky-400/10',
  Pagada: 'text-emerald-300 border-emerald-400/20 bg-emerald-400/10',
  Rechazada: 'text-gray-400 border-white/10 bg-white/[0.04]',
};
const inputCls = 'bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40';

// Solicitud de retiro de una línea del saldo. Primero simula (bricks que se perderían) y, si hay bricks, pide confirmarlo.
function WithdrawForm({ b, onDone, onCancel }) {
  const [amount, setAmount] = useState(String(b.available));
  const [preview, setPreview] = useState(null);
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const kind = b.bucket === 'intereses' ? 'intereses' : 'principal';
  const value = Math.round(Number(String(amount).replace(',', '.')) * 100) / 100;
  const valid = value > 0 && value <= b.available + 0.001;
  const body = (extra = {}) => ({ kind, bucket: b.bucket, currency: b.currency, amount: value, ...extra });

  useEffect(() => {
    setPreview(null); setAccept(false);
    if (!valid || kind === 'intereses') return undefined;
    const t = setTimeout(() => {
      dashboardApi.requestWithdrawal(body({ dry_run: true })).then(setPreview).catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(t);
  }, [value]); // la simulación depende solo del importe

  const bricks = preview?.bricks_lost || 0;
  const submit = async (e) => {
    e.preventDefault();
    if (!valid || (bricks > 0 && !accept)) return;
    setBusy(true);
    try {
      await dashboardApi.requestWithdrawal(body({ accept_bricks_loss: bricks > 0 && accept }));
      toast.success('Solicitud de retiro enviada. El equipo la revisará y te avisará cuando esté pagada.');
      onDone();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 w-full rounded-xl border border-[#c9a84c]/20 bg-[#c9a84c]/[0.04] p-4 space-y-3" data-testid={`retiro-form-${b.bucket}`}>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-400 space-y-1">
          <span>Importe a retirar (máx. {money(b.available, b.currency)})</span>
          <input type="number" min="0.01" step="0.01" max={b.available} value={amount} onChange={(e) => setAmount(e.target.value)}
            className={`${inputCls} w-44 block`} data-testid="retiro-importe" />
        </label>
        <button type="button" onClick={() => setAmount(String(b.available))} className="text-xs text-[#c9a84c] hover:underline pb-2">Todo</button>
      </div>
      {bricks > 0 && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/[0.07] p-3 text-sm" role="alert" data-testid="retiro-aviso-bricks">
          <p className="flex items-center gap-2 text-amber-100 font-medium"><AlertTriangle size={15} /> Perderás {bricks.toLocaleString('es-ES', { maximumFractionDigits: 2 })} bricks</p>
          <p className="text-xs text-gray-300 mt-1">Esta parte de tu aportación genera bricks. Si la retiras, esos bricks desaparecen y no se recuperan.</p>
          <label className="mt-2 flex items-start gap-2 text-xs text-gray-200">
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5" data-testid="retiro-acepto" />
            Entiendo que perderé esos bricks y quiero solicitar el retiro.
          </label>
        </div>
      )}
      <div className="flex flex-wrap gap-2 justify-end">
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-white hover:bg-white/[0.05]"><X size={14} /> Cancelar</button>
        <button type="submit" disabled={!valid || busy || (bricks > 0 && !accept)} className="rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-50" data-testid="retiro-enviar">
          {busy ? 'Enviando…' : 'Solicitar retiro'}
        </button>
      </div>
    </form>
  );
}

function BalanceRow({ b, open, onOpen, onClose, onDone }) {
  return (
    <div className="px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-white font-medium">{b.label}</p>
          <p className="text-xs text-gray-400 mt-0.5">{BUCKET_NOTE[b.bucket]}</p>
          {b.retirable && b.bucket !== 'intereses' && (
            <p className="text-xs text-gray-400 mt-1">
              {b.available > 0 ? <span className="text-emerald-300">{money(b.available, b.currency)} disponibles para retirar</span> : null}
              {b.available > 0 && b.locked > 0 ? ' · ' : null}
              {b.locked > 0 ? `${money(b.locked, b.currency)} se podrán retirar a partir del ${fdate(b.next_unlock)}` : null}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-lg font-semibold text-white tabular-nums">{money(b.amount, b.currency)}</p>
          {b.retirable && b.available > 0 && !open && (
            <button type="button" onClick={onOpen} className="rounded-lg border border-[#c9a84c]/40 px-3 py-1.5 text-sm text-[#c9a84c] hover:bg-[#c9a84c]/10" data-testid={`retirar-${b.bucket}-${b.currency}`}>Retirar</button>
          )}
        </div>
      </div>
      {open && <WithdrawForm b={b} onCancel={onClose} onDone={onDone} />}
    </div>
  );
}
const STATUS_STYLE = {
  'En ejecución': 'text-emerald-300 border-emerald-400/20 bg-emerald-400/10',
  'Abierta a aportaciones': 'text-[#c9a84c] border-[#c9a84c]/30 bg-[#c9a84c]/10',
  Cerrada: 'text-gray-300 border-white/10 bg-white/[0.04]',
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLE[status] || 'text-sky-200 border-sky-400/20 bg-sky-400/10'}`}>
      {status}
    </span>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-xs text-gray-400">{label}</span>
      <span className={`tabular-nums text-right ${strong ? 'text-white font-semibold' : 'text-gray-200'}`}>{value}</span>
    </div>
  );
}

function PositionCard({ p, i }) {
  return (
    <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: Math.min(i, 6) * 0.04 }}
      className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 flex flex-col" data-testid={`posicion-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-white font-semibold leading-snug">{p.operation}</h3>
          {p.location && <p className="mt-1 flex items-center gap-1 text-xs text-gray-400"><MapPin size={12} />{p.location}</p>}
        </div>
        <StatusPill status={p.status} />
      </div>
      <div className="mt-4 divide-y divide-white/[0.05]">
        <Row label="Tu aportación" value={money(p.amount, p.currency)} strong />
        <Row label="Fecha de entrada" value={fdate(p.date)} />
        <Row label="Tu parte del resultado" value={`${p.pct_member} %`} />
        {p.closed ? (
          <>
            <Row label="Resultado de tu participación" value={money(p.gross_result, p.currency)} />
            <Row label={`Te corresponde (${p.pct_member} %)`} value={money(p.member_result, p.currency)} strong />
            {p.closed_at && <Row label="Cerrada el" value={fdate(p.closed_at)} />}
          </>
        ) : (
          <p className="pt-3 text-xs text-gray-400 leading-relaxed">El resultado se muestra aquí cuando la operación se cierre.</p>
        )}
      </div>
      {p.opportunity_id && (
        <Link to={`/dashboard/propiedades/${p.opportunity_id}`} className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#c9a84c] hover:underline self-start">
          Ver la ficha de la operación <ArrowRight size={14} />
        </Link>
      )}
    </motion.article>
  );
}

export default function DashboardMisOperaciones() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(null); // clave de la línea del saldo con el formulario abierto

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    dashboardApi.myOperations()
      .then(setData)
      .catch((e) => setError(e.message || 'Error'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const header = <SectionHeader title="Mis operaciones" subtitle="Lo que has aportado a las operaciones del club, en qué estado están y su resultado cuando se cierran." />;
  if (loading) return <div data-testid="dashboard-operaciones">{header}<PageSkeleton cards={3} rows={3} /></div>;
  if (error) return <div className="space-y-6" data-testid="dashboard-operaciones">{header}<LoadError onRetry={load} /></div>;

  const s = data.summary || {};
  const positions = data.positions || [];
  const balances = data.balances || [];
  const open = positions.filter((p) => !p.closed);
  const closed = positions.filter((p) => p.closed);

  if (!positions.length && !balances.length) {
    return (
      <div className="space-y-6" data-testid="dashboard-operaciones">
        {header}
        <EmptyState icon={Building2}
          title={data.source === 'none' ? 'Aún no vemos aportaciones a tu nombre' : 'Todavía no participas en ninguna operación'}
          text={data.source === 'none'
            ? 'Si ya has aportado a alguna operación del club y no aparece aquí, escríbenos a soporte y lo revisamos.'
            : 'Cuando participes en una operación del club, aquí verás tu aportación, su estado y, al cerrarse, el resultado que te corresponde.'}
          action={<Link to="/dashboard/propiedades" className="inline-flex rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] hover:bg-[#d8b95d]">Ver oportunidades</Link>} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-operaciones">
      {header}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 -mt-4">
        <MetricCard label="En operaciones en curso" value={joinMoney(s.in_progress)} sub={`${s.operations_open || 0} ${s.operations_open === 1 ? 'operación' : 'operaciones'}`} icon={Layers} delay={0} />
        <MetricCard label="Operaciones cerradas" value={s.operations_closed || 0} sub={s.operations_closed ? 'con su resultado' : 'Ninguna todavía'} icon={CheckCircle2} delay={1} />
        <MetricCard label="Te corresponde" value={s.results?.length ? joinMoney(s.results) : '—'} sub="De las operaciones cerradas" icon={Building2} delay={2} />
        <MetricCard label="Tu saldo" value={balances.length ? joinMoney(byCurrency(balances)) : '0 €'} sub="Fuera de las operaciones en curso" icon={Wallet} delay={3} />
      </div>

      {open.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">En curso</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{open.map((p, i) => <PositionCard key={p.id} p={p} i={i} />)}</div>
        </section>
      )}

      {closed.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">Cerradas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{closed.map((p, i) => <PositionCard key={p.id} p={p} i={i} />)}</div>
        </section>
      )}

      {balances.length > 0 && (
        <section className="space-y-3" data-testid="saldo-pendiente">
          <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">Tu saldo</h3>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.05]">
            {balances.map((b) => {
              const key = `${b.bucket}-${b.currency}`;
              return <BalanceRow key={key} b={b} open={withdrawing === key} onOpen={() => setWithdrawing(key)}
                onClose={() => setWithdrawing(null)} onDone={() => { setWithdrawing(null); load(); }} />;
            })}
          </div>
          <p className="text-xs text-gray-500">
            Puedes destinar tu saldo a las próximas operaciones del club. Los intereses se pueden retirar siempre; lo aportado,
            a partir de {data.lockup_months || 12} meses desde la aportación. El bono incluido con la membresía no se puede retirar.
          </p>
        </section>
      )}

      {(data.withdrawals || []).length > 0 && (
        <section className="space-y-3" data-testid="mis-retiros">
          <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">Mis solicitudes de retiro</h3>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.05]">
            {data.withdrawals.map((w) => (
              <div key={w.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <p className="text-sm text-white">{w.kind === 'intereses' ? 'Intereses' : (w.bucket === 'w2usdt' ? 'W2USDT' : 'Dinero aportado')}</p>
                  <p className="text-xs text-gray-400">Solicitada el {fdate(w.requested_at)}{w.paid_at ? ` · pagada el ${fdate(w.paid_at)}` : ''}{w.bricks_lost > 0 ? ` · ${w.bricks_lost.toLocaleString('es-ES')} bricks` : ''}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tabular-nums text-white">{money(w.amount, w.currency)}</span>
                  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${W_STATUS[w.status] || W_STATUS.Rechazada}`}>{w.status}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="text-[11px] text-gray-500 leading-relaxed">
        Cifras del registro del club a día de hoy. El resultado de cada operación se reparte según el porcentaje que se fijó cuando entraste en ella.
        Si algo no cuadra con lo que tienes, escríbenos a soporte y lo revisamos.
      </p>
    </div>
  );
}
